/**
 * Routing Configuration Panel
 *
 * Provides controls for InfiniBand/RoCE adaptive routing configuration,
 * including routing algorithms, SHARP, congestion control, and QoS settings.
 */

import { useState } from 'react';
import { Section } from '../layout/Section';
import { useStore } from '../../store/store';

export function RoutingConfigPanel() {
  const { configuration, updateNetwork } = useStore();
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show for InfiniBand or RoCE fabrics
  const showForEastWest =
    configuration.network.eastWest.fabricType === 'InfiniBand' ||
    configuration.network.eastWest.fabricType === 'RoCE';

  if (!showForEastWest) {
    return null;
  }

  const routing = configuration.network.eastWest.routing || {
    algorithm: 'adaptive',
    enableSHARP: false,
    enableCongestionControl: true,
    qosLevels: 8,
    enableDynamicRerouting: true,
    enablePathRandomization: true,
  };

  const updateRouting = (updates: Partial<typeof routing>) => {
    updateNetwork({
      eastWest: {
        ...configuration.network.eastWest,
        routing: {
          ...routing,
          ...updates,
        },
      },
    });
  };

  return (
    <Section
      title="Adaptive Routing Configuration"
      collapsible
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-3">
        {/* Routing Algorithm */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Routing Algorithm
          </label>
          <select
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
            value={routing.algorithm}
            onChange={(e) => updateRouting({ algorithm: e.target.value })}
          >
            <option value="static">Static Routing</option>
            <option value="adaptive">Adaptive Routing</option>
            <option value="ftree">Fat-Tree Optimized</option>
            <option value="dfp">Dispersive Routing (DFP)</option>
            <option value="ar-updn">Adaptive Up/Down</option>
          </select>
          <div className="text-[10px] text-gray-600 mt-1">
            {routing.algorithm === 'static' &&
              'Fixed routing paths, no congestion adaptation'}
            {routing.algorithm === 'adaptive' &&
              'Dynamic path selection based on link load and congestion'}
            {routing.algorithm === 'ftree' &&
              'Optimized for fat-tree topologies with minimal path diversity'}
            {routing.algorithm === 'dfp' &&
              'Destination-based adaptive routing for high throughput'}
            {routing.algorithm === 'ar-updn' &&
              'Adaptive routing with up/down rules, prevents routing loops'}
          </div>
        </div>

        {/* SHARP */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enableSHARP"
            checked={routing.enableSHARP}
            onChange={(e) => updateRouting({ enableSHARP: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="enableSHARP" className="text-xs text-gray-700">
            Enable SHARP (Scalable Hierarchical Aggregation Protocol)
          </label>
        </div>
        <div className="text-[10px] text-gray-600 ml-5">
          Hardware-accelerated collective operations (reduce, broadcast, etc.) for AI/ML
          workloads
        </div>

        {/* Congestion Control */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enableCongestionControl"
            checked={routing.enableCongestionControl}
            onChange={(e) => updateRouting({ enableCongestionControl: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="enableCongestionControl" className="text-xs text-gray-700">
            Enable Congestion Control
          </label>
        </div>
        <div className="text-[10px] text-gray-600 ml-5">
          Automatic flow control to prevent network congestion and head-of-line blocking
        </div>

        {/* QoS Levels */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            QoS Service Levels
          </label>
          <select
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
            value={routing.qosLevels}
            onChange={(e) => updateRouting({ qosLevels: parseInt(e.target.value) })}
          >
            <option value="4">4 Levels</option>
            <option value="8">8 Levels (Standard)</option>
            <option value="16">16 Levels (Extended)</option>
          </select>
          <div className="text-[10px] text-gray-600 mt-1">
            Number of priority levels for traffic classification and scheduling
          </div>
        </div>

        {/* Dynamic Rerouting */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enableDynamicRerouting"
            checked={routing.enableDynamicRerouting}
            onChange={(e) => updateRouting({ enableDynamicRerouting: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="enableDynamicRerouting" className="text-xs text-gray-700">
            Enable Dynamic Rerouting on Failure
          </label>
        </div>
        <div className="text-[10px] text-gray-600 ml-5">
          Automatically reroute traffic when links or switches fail
        </div>

        {/* Path Randomization */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="enablePathRandomization"
            checked={routing.enablePathRandomization}
            onChange={(e) => updateRouting({ enablePathRandomization: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="enablePathRandomization" className="text-xs text-gray-700">
            Enable Path Randomization
          </label>
        </div>
        <div className="text-[10px] text-gray-600 ml-5">
          Randomize path selection for better load distribution across equal-cost paths
        </div>

        {/* Info Box */}
        <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
          <div className="font-semibold text-blue-900 mb-1">Adaptive Routing Benefits</div>
          <ul className="text-gray-700 text-[10px] space-y-1 list-disc list-inside">
            <li>Improves network throughput by up to 30-50% under congestion</li>
            <li>Reduces tail latency for latency-sensitive applications</li>
            <li>Better fault tolerance with automatic failover</li>
            <li>SHARP reduces collective operation latency by ~10x for AI training</li>
          </ul>
        </div>
      </div>
    </Section>
  );
}
